import type { VideoUploadLimits } from '@/types';
import { VIDEO_TRANSLATION_DEFAULTS } from '@/video/translations';

import { MENTION_FALLBACK_LABEL } from '../../utils/mentions/mentionDisplay';

export const ICON_CLASS = 'size-4';

export const DEFAULT_GIF_SEARCH_LIMIT = 24;
export const DEFAULT_GIF_SEARCH_DEBOUNCE_MS = 300;
export const DEFAULT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const DEFAULT_UPLOAD_ACCEPT = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';

// The composer's limits until the host passes the server's limits.
export const COMMUNITY_VIDEO_UPLOAD_LIMITS: VideoUploadLimits = {
  max_bytes: 2 * 1024 * 1024 * 1024,
  max_duration_in_ms: 60 * 60 * 1000,
};

export const POPOVER_PANEL_CLASSES = 'rounded-3xl bg-white border border-black/5 shadow-[0_4px_6px_rgba(0,0,0,0.08)]';

export const TRANSLATION_DEFAULTS: Record<string, string> = {
  ...VIDEO_TRANSLATION_DEFAULTS,
  'community.composer.close': 'Close',
  'community.composer.destination_feed': 'Feed',
  'community.composer.choose_destination': 'Choose where to post',
  'community.action_menu.move_post_read_only_hint': "Posts can't be moved into a read only channel.",
  'community.composer.post_title_placeholder': 'Title',
  'community.composer.submit': 'Post',
  'community.composer.save': 'Save',
  'community.composer.schedule.open': 'Schedule post',
  'community.composer.schedule.submit': 'Schedule',
  'community.composer.schedule.submitting': 'Scheduling...',
  'community.composer.schedule.chip': 'Posts {{date}}',
  'community.composer.schedule.change_time': 'Change time, {{label}}',
  'community.composer.schedule.title': 'When should this publish?',
  'shared.cta.back': 'Back',
  'shared.cta.cancel': 'Cancel',
  'shared.cta.confirm': 'Confirm',
  'smartDateTimeField.legend': 'Publish time',
  'smartDateTimeField.selected': 'Selected {{date}}',
  'smartDateTimeField.placeholder': 'Try "tomorrow at 2pm"',
  'smartDateTimeField.clear': 'Clear date',
  'smartDateTimeField.open_calendar': 'Choose a date',
  'smartDateTimeField.time_label': 'Time',
  'smartDateTimeField.time_placeholder': '2pm',
  'smartDateTimeField.error_unparseable': 'Couldn\'t read that. Try "tomorrow at 2pm".',
  'smartDateTimeField.error_unexpected': 'Something went wrong. Please try again.',
  'smartDateTimeField.error_time': 'Enter a valid time',
  'schedulePicker.summary_time': 'Publishes {{relative}}',
  'schedulePicker.summary_empty': 'Choose a publish time',
  'schedulePicker.summary_empty_hint': '',
  'community.composer.schedule.load_error': "Couldn't load the scheduler. Refresh the page and try again.",
  'community.composer.schedule.error_passed': 'That time has passed. Pick a new time.',
  'community.composer.schedule.toast': 'Post scheduled',
  'community.composer.schedule.pick_specific': 'Pick a specific time…',
  'community.composer.schedule.pick_specific_hint': 'Type it in or use the calendar',
  'community.composer.schedule.next_usual': 'Next usual time',
  'community.composer.schedule.next_usual_hint': '{{date}} · when your team usually posts',
  'community.composer.schedule.view_queue': 'View all scheduled posts',
  'community.composer.schedule.view_queue_count': 'View all scheduled posts ({{count}})',
  'community.composer.schedule.view_queue_short_count': 'Scheduled posts ({{count}})',
  'community.composer.schedule.queue.title': 'Scheduled posts',
  'community.composer.schedule.queue.description':
    "Your whole team can see what's queued. Only the author or an admin can edit a scheduled post.",
  'community.composer.schedule.queue.you': 'You',
  'community.composer.schedule.queue.deleted_channel': '#{{- channel}} (deleted)',
  'community.composer.schedule.queue.reschedule': 'Reschedule',
  'community.composer.schedule.queue.publish_now': 'Publish now',
  'community.composer.schedule.queue.only_author_or_admin': 'Only the author or an admin can edit this post',
  'community.composer.schedule.queue.failed': "Couldn't publish",
  'community.composer.schedule.queue.unpublishable': "Can't be published",
  'community.composer.schedule.queue.failure_reason.channel_unavailable': 'The channel is no longer available',
  'community.composer.schedule.queue.failure_reason.author_unavailable': 'The author is no longer available',
  'community.composer.schedule.queue.failure_reason.attachable_unavailable':
    'The attached content is no longer available',
  'community.composer.schedule.queue.failure_reason.community_unavailable': 'The community is no longer available',
  'community.composer.schedule.queue.failure_reason.mentions_unavailable':
    'The author can no longer mention everyone or badges',
  'community.composer.schedule.queue.failure_reason.unknown': 'Something went wrong. Edit the post to try again.',
  'community.composer.schedule.queue.failure_reason.time_passed': 'Edit it to pick a new time',
  'community.composer.schedule.queue.empty_title': 'Nothing scheduled yet',
  'community.composer.schedule.queue.empty_description':
    "Posts anyone on your team schedules show up here, so you can see what's coming up.",
  'community.composer.schedule.queue.schedule_post': 'Schedule a post',
  'community.composer.schedule.queue.takeover_title': 'Become the author?',
  'community.composer.schedule.queue.takeover_description':
    "The author is no longer available. You'll become the author of this post.",
  'community.composer.schedule.queue.takeover_confirm': 'Become the author',
  'community.composer.schedule.queue.takeover_off_team_description':
    "The author is no longer on the team. You'll become the author of this post.",
  'community.action_menu.actions': 'Actions',
  'community.action_menu.edit_post': 'Edit post',
  'community.action_menu.delete_post': 'Delete post',
  'community.roles.admin': 'Admin',
  'community.roles.manager': 'Community manager',
  'community.roles.moderator': 'Moderator',
  'community.composer.submitting': 'Posting…',
  'community.composer.saving': 'Saving…',
  'community.composer.format_text': 'Format text',
  'community.composer.format_bold': 'Bold',
  'community.composer.format_italic': 'Italic',
  'community.composer.format_underline': 'Underline',
  'community.composer.format_strike': 'Strikethrough',
  'community.composer.format_code': 'Inline code',
  'community.composer.add_media': 'Add media',
  'community.composer.media_tab_gif': 'GIF',
  'community.composer.media_tab_upload': 'Upload',
  'community.composer.gif_search_placeholder': 'Search GIFs',
  'community.composer.gif_loading': 'Loading…',
  'community.composer.gif_error': 'Could not load GIFs.',
  'community.composer.gif_no_results': 'No results found.',
  'community.composer.gif_powered_by': 'Powered by GIPHY',
  'community.composer.media_panel.tabs': 'Media source',
  'community.composer.upload_loading': 'Uploading…',
  'community.composer.upload_choose_file': 'Choose file',
  'community.composer.upload_hint': 'JPG, PNG, GIF, or WebP — up to 10 MB',
  'community.composer.upload_error': 'Could not upload that file.',
  'community.composer.upload_file_too_large': 'File size is greater than 10 MB',
  'community.composer.add_video': 'Add video',
  'community.composer.add_link': 'Add link',
  'community.composer.link_url_placeholder': 'https://example.com',
  'community.composer.link_save': 'Save',
  'community.composer.link_invalid': 'Enter a valid link',
  'community.composer.link_remove': 'Remove',
  'community.composer.add_emoji': 'Add emoji',
  'community.composer.emoji_search_placeholder': 'Search emoji',
  'community.composer.emoji_no_results': 'No emoji found.',
  'community.composer.attach_post': 'Attach a post',
  'community.composer.post_search_placeholder': 'Search posts',
  'community.composer.post_loading': 'Loading…',
  'community.composer.post_no_results': 'No posts found.',
  'community.composer.post_max_reached': 'Remove the current post to attach another.',
  'community.composer.attached_post_remove': 'Remove attached post',
  'community.composer.link_preview_loading': 'Loading preview…',
  'community.composer.link_preview_remove': 'Remove link preview',
  'community.composer.embed_prompt_paste_as': 'Paste as:',
  'community.composer.embed_prompt_preview': 'Preview only',
  'community.composer.embed_prompt_both': 'Preview + URL',
  'community.composer.embed_prompt_keep_link': 'URL only',
  'community.composer.announcement_confirmation.title_one': 'Notify {{count, number}} member?',
  'community.composer.announcement_confirmation.title_other': 'Notify {{count, number}} members?',
  'community.composer.announcement_confirmation.description_everyone':
    'Everyone who can see this post will get a notification.',
  'community.composer.announcement_confirmation.description_everyone_in_channel':
    'Members who can see {{- channel}} will get a notification.',
  'community.composer.announcement_confirmation.description_badges':
    'Members with {{- badges, list}} who can see this post will get a notification.',
  'community.composer.announcement_confirmation.tagged_twice': 'Anyone tagged twice is notified once.',
  'community.composer.announcement_confirmation.recent_use_one':
    '@{{- name}} was used {{count}} time in this community in the last 24 hours.',
  'community.composer.announcement_confirmation.recent_use_other':
    '@{{- name}} was used {{count}} times in this community in the last 24 hours.',
  'community.composer.announcement_confirmation.nobody_title': 'Nobody will be notified',
  'community.composer.announcement_confirmation.nobody_has_badges_one': 'Nobody has the {{- badges, list}} badge yet.',
  'community.composer.announcement_confirmation.nobody_has_badges_other':
    'Nobody has the {{- badges, list}} badges yet.',
  'community.composer.announcement_confirmation.nobody_can_see': 'Nobody else can see this post yet.',
  'community.composer.announcement_confirmation.nobody_new_title': 'Nobody new will be notified',
  'community.composer.announcement_confirmation.already_notified':
    'Everyone this reaches was already notified about this post.',
  'community.composer.announcement_confirmation.post_and_notify': 'Post and notify',
  'community.composer.announcement_confirmation.post_anyway': 'Post anyway',
  'community.composer.announcement_confirmation.save_and_notify': 'Save and notify',
  'community.composer.announcement_confirmation.save_anyway': 'Save anyway',
  'community.composer.announcement_confirmation.schedule_and_notify': 'Schedule and notify',
  'community.composer.announcement_confirmation.schedule_anyway': 'Schedule anyway',
  'community.composer.announcement_confirmation.notified_on_publish': 'Notifications go out when the post publishes.',
  'community.composer.announcement_confirmation.recent_published_use_one':
    '@{{- name}} was used in {{count}} published post in this community in the last 24 hours.',
  'community.composer.announcement_confirmation.recent_published_use_other':
    '@{{- name}} was used in {{count}} published posts in this community in the last 24 hours.',
  'community.join.former_member': MENTION_FALLBACK_LABEL,
  'community.mentions.list_label': 'Members to mention',
};

export const tr = (translations: Record<string, string> | undefined, key: string): string =>
  translations?.[key] ?? TRANSLATION_DEFAULTS[key] ?? key;
